import { LoadableArrayLoadStrategy } from '@aiwyn/shared';
import {
  ClientRequestDetails,
  ClientRequestItemResponse,
  ClientRequestSubmitAnswerPayload,
  DeliverableModel,
  ItemSubmissionFile,
  MarkDeliverableFilesActions,
  RequestSubmissionFile,
  SecureLinkUser,
} from '@aiwyn/api';
import { StateOperator } from '@ngxs/store';
import { CommonClientRequestsStateModel } from './common-client-requests.model';

export class GetAllClientRequestGroups {
  static readonly type = '[CommonClientRequestsState] GetAllClientRequestGroups';

  constructor(public readonly loadStrategy: LoadableArrayLoadStrategy = LoadableArrayLoadStrategy.LOAD) {}
}

export class GetAllClientRequests {
  static readonly type = '[CommonClientRequestsState] GetAllClientRequests';

  constructor(public readonly loadStrategy: LoadableArrayLoadStrategy = LoadableArrayLoadStrategy.LOAD) {}
}

export class GetClientRequests {
  static readonly type = '[CommonClientRequestsState] GetClientRequests';

  constructor(
    public readonly requestIds: number[],
    public readonly loadStrategy: LoadableArrayLoadStrategy = LoadableArrayLoadStrategy.LOAD,
    public readonly requestItemAssigneeIds: number[] = [],
  ) {}
}

export class GetClientRequestDetails {
  static readonly type = '[CommonClientRequestsState] GetClientRequestDetails';

  constructor(
    public readonly requestId: number,
    public readonly loadStrategy: LoadableArrayLoadStrategy = LoadableArrayLoadStrategy.REPLACE,
    public readonly requestItemIds?: number[],
  ) {}
}

export class GetClientRequestDetailsSuccess {
  static readonly type = '[CommonClientRequestsState] GetClientRequestDetailsSuccess';

  constructor(
    public readonly clientRequestId: number,
    public readonly details: ClientRequestDetails,
  ) {}
}

export class GetClientRequestMetadata {
  static readonly type = '[CommonClientRequestsState] GetClientRequestMetadata';

  constructor(
    public readonly requestId: number,
    public readonly loadStrategy: LoadableArrayLoadStrategy = LoadableArrayLoadStrategy.REPLACE,
  ) {}
}

export class GetQuestionnaireResponses {
  static readonly type = '[CommonClientRequestsState] GetQuestionnaireResponses';

  constructor(
    public readonly clientRequestId: number,
    public readonly loadStrategy: LoadableArrayLoadStrategy = LoadableArrayLoadStrategy.REPLACE,
  ) {}
}

export class AddClientRequestMetadataFiles {
  static readonly type = '[CommonClientRequestsState] AddClientRequestFiles';

  constructor(
    public readonly requestId: number,
    public readonly fileIds: number[],
  ) {}
}

export class GetClients {
  static readonly type = '[CommonClientRequestsState] GetClients';

  constructor(public readonly loadStrategy: LoadableArrayLoadStrategy = LoadableArrayLoadStrategy.LOAD) {}
}

export class GetAssignees {
  static readonly type = '[CommonClientRequestsState] GetAssignees';

  constructor(public readonly loadStrategy: LoadableArrayLoadStrategy = LoadableArrayLoadStrategy.LOAD) {}
}

export class GetStaff {
  static readonly type = '[CommonClientRequestsState] GetStaff';

  constructor(public readonly loadStrategy: LoadableArrayLoadStrategy = LoadableArrayLoadStrategy.LOAD) {}
}

export class GetServiceLines {
  static readonly type = '[CommonClientRequestsState] GetServiceLines';
}

export class GetAllDeliverables {
  static readonly type = '[CommonClientRequestsState] GetDeliverables';

  constructor(public readonly loadStrategy: LoadableArrayLoadStrategy = LoadableArrayLoadStrategy.LOAD) {}
}

export class GetFiles {
  static readonly type = '[CommonClientRequestsState] GetFiles';

  constructor(
    public readonly fileIds: number[],
    public readonly loadStrategy: LoadableArrayLoadStrategy = LoadableArrayLoadStrategy.LOAD,
  ) {}
}

export class InsertCategorizedFilesIntoClientRequest {
  static readonly type = '[CommonClientRequestsState] InsertFilesIntoClientRequestDocuments';

  constructor(
    public readonly clientRequestId: number,
    public readonly documentSubmissionFiles: ItemSubmissionFile[],
  ) {}
}

export class InsertUncategorizedFilesIntoClientRequest {
  static readonly type = '[CommonClientRequestsState] InsertUncategorizedFilesIntoClientRequest';

  constructor(
    public readonly clientRequestId: number,
    public readonly requestSubmissionFiles: RequestSubmissionFile[],
  ) {}
}

export class RemoveClientRequestFiles {
  static readonly type = '[CommonClientRequestsState] RemoveClientRequestFiles';

  constructor(
    public readonly clientRequestId: number,
    public readonly fileIds: number[],
  ) {}
}



export class UnCategorizeFilesFromClientRequestDocuments {
  static readonly type = '[CommonClientRequestsState] UnCategorizeFilesFromClientRequestDocuments';

  constructor(
    public readonly clientRequestId: number,
    /**
     * number[] => remove files from all documents
     * Record<string, number[]> => remove files from corresponding documents
     */
    public readonly categorizedFilesToRemove: number[] | Record<string, number[]>, // { [documentId]: fileId[]; }
  ) {}
}

export class InsertAnswersIntoRequestItems {
  static readonly type = '[CommonClientRequestsState] InsertAnswersIntoRequestItems';

  constructor(
    public readonly clientRequestItemResponseList: ClientRequestItemResponse[],
    public readonly clientRequestSubmitAnswerPayload: ClientRequestSubmitAnswerPayload,
  ) {}
}

export class SetSecureLinkUserForRequestItem {
  static readonly type = '[ClientRequestsState] SetSecureLinkUserForRequestItem';

  constructor(public secureLinkUser: SecureLinkUser, public assigneesId: number[], public readonly clientRequestId: number) {}
}

export class RemoveSecureLinkUserForRequestItem {
  static readonly type = '[ClientRequestsState] RemoveSecureLinkUserForRequestItem';

  constructor(public secureLinkUser: SecureLinkUser, public assigneesId: number[], public readonly clientRequestId: number) {}
}

export class MarkDeliverableFiles {
  static readonly type = '[CommonClientRequestsState] MarkDeliverableFiles';

  constructor(
    public readonly deliverableId: number,
    public readonly fileIds: number[],
    public readonly action: MarkDeliverableFilesActions
  ) {}
}

export class PatchClientRequestState {
  static readonly type = '[CommonClientRequestsState] PatchClientRequestState';

  constructor(public readonly patchedState: StateOperator<CommonClientRequestsStateModel>) {}
}

export class RemoveClientRequestFilesSuccess {
  static readonly type = '[CommonClientRequestsState] RemoveClientRequestFilesSuccess';
}

export class SetClientRequestItemNotApplicableSuccess {
  static readonly type = '[ClientRequestsState] SetClientRequestItemNotApplicableSuccess';
  constructor(public readonly requestItemId: number) {}
}

export class UpdateDeliverablesFromSubscription {
  static readonly type = '[CommonClientRequestsState] UpdateDeliverablesFromSubscription';
  constructor(public readonly deliverables: DeliverableModel[]) {}
}
